import { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Label, Spinner, Button,
} from "reactstrap";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import {
  createInvoice,
  updateInvoice,
  uploadFileToInvoice,
  removeInvoiceAttachment,
  getInvoiceSignedUrls,
  uploadStagingFile,
  getStagingSignedUrls,
  removeStagingObject,
  formatCurrency,
  computeLineMoney,
  computeLineTotal,
} from "./Api";
import { Previewer } from "../../../../components/DocumentPreviewer/Previewer";
import { getFileExtension, imageTypes, pdfTypes, officeTypes, videoTypes }
  from "../../../../components/DocumentPreviewer/utils";

const selectStyles = {
  control: (base, { isFocused, isDisabled }) => ({
    ...base,
    borderColor: isFocused ? "#6366F1" : "#E2E8F0",
    borderRadius: 8,
    minHeight: 38,
    fontSize: "0.83rem",
    boxShadow: isFocused ? "0 0 0 2px rgba(99,102,241,0.15)" : "none",
    backgroundColor: isDisabled ? "#f8f9fa" : base.backgroundColor,
    "&:hover": { borderColor: "#6366F1" },
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    fontSize: "0.83rem",
    background: isSelected ? "#4F46E5" : isFocused ? "#EEF2FF" : "white",
    color: isSelected ? "white" : "#0F172A",
  }),
  menu: (base) => ({ ...base, borderRadius: 8, border: "1px solid #E2E8F0", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const LABEL_STYLE = {
  fontSize: "0.72rem", fontWeight: 700, color: "#334155",
  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6,
};

const INPUT_STYLE = {
  borderColor: "#E2E8F0",
  borderRadius: 8,
  fontSize: "0.85rem",
  height: 38,
};

const getFileTypeFromKey = (keyOrName) => {
  const ext = getFileExtension(keyOrName).toLowerCase();
  if (pdfTypes.includes(ext)) return "pdf";
  if (imageTypes.includes(ext)) return "image";
  if (videoTypes.includes(ext)) return "video";
  if (officeTypes.includes(ext)) return "office";
  return "unknown";
};

const FilePreviewModal = ({ isOpen, toggle, signedUrl, fileKey }) => {
  const type = fileKey ? getFileTypeFromKey(fileKey) : "unknown";
  const fileName = fileKey ? fileKey.split("/").pop() : "Attachment";
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 12000 }}>
      <ModalHeader toggle={toggle} style={{ background: "#0F172A", borderBottom: "none", padding: "14px 20px" }}>
        <span style={{ color: "white", fontWeight: 700, fontSize: "0.9rem" }}>{fileName}</span>
      </ModalHeader>
      <ModalBody style={{ padding: 0, background: "#0F172A" }}>
        {signedUrl ? (
          <Previewer signedUrl={signedUrl} type={type} height="72vh" width="100%" />
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "72vh" }}>
            <Spinner style={{ color: "#A5B4FC" }} />
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ background: "#0F172A", border: "none", padding: "12px 20px" }}>
        <Button color="secondary" onClick={toggle}>Close</Button>
        {signedUrl && (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={fileName}
            className="iv-btn iv-btn--primary"
            style={{ textDecoration: "none" }}
          >
            <FeatherIcon icon="download" size={14} style={{ marginRight: 4 }} />
            Download
          </a>
        )}
      </ModalFooter>
    </Modal>
  );
};

const blankLine = (propertyId = "", buildingName = "") => ({
  propertyId,
  buildingName,
  unitNo: "",
  hours: "",
  amountPerHour: "",
  gstRate: 0,
  comment: "",
});

const InvoiceFormModal = ({
  isOpen,
  onClose,
  invoiceData,
  isNew,
  properties = [],
  vendors,
  vendorLocked = false,
  vendorName = "",
  vendorCategoryName = "",
  vendorCategories = [],
  prefilledPropertyId,
  prefilledBuildingName,
  prefilledPropertyLabel,
}) => {
  const initial = useMemo(() => ({
    title: "",
    vendorId: "",
    categoryId: "",
    description: "",
    referenceNo: "",
    lines: [prefilledPropertyId ? blankLine(prefilledPropertyId, prefilledBuildingName || "") : blankLine()],
  }), [prefilledPropertyId, prefilledBuildingName]);

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const submitLock = useRef(false);
  const [createdId, setCreatedId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [signedUrls, setSignedUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [previewPendingId, setPreviewPendingId] = useState(null);
  const fileInputRef = useRef(null);

  const cleanupStaged = (files) => {
    (files || []).forEach((f) => {
      if (f.key) removeStagingObject(f.key).catch(() => {});
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    if (isNew) {
      setForm(initial);
      setCreatedId(null);
      setAttachments([]);
      setSignedUrls({});
      cleanupStaged(pendingFiles);
      setPendingFiles([]);
    } else if (invoiceData) {
      const refs = Array.isArray(invoiceData.properties) && invoiceData.properties.length
        ? invoiceData.properties
        : [];
      const firstRef = refs[0] || {
        propertyId: prefilledPropertyId || "",
        buildingName: prefilledBuildingName || "",
      };
      setForm({
        title: invoiceData.title || "",
        vendorId: invoiceData.vendorId || invoiceData.vendor?._id || "",
        categoryId: invoiceData.categoryId || invoiceData.category?._id || "",
        description: invoiceData.description || "",
        referenceNo: invoiceData.referenceNo || "",
        lines: [{
          propertyId: firstRef.propertyId || "",
          buildingName: firstRef.buildingName || "",
          unitNo: firstRef.unitNo || "",
          hours: invoiceData.hours ?? "",
          amountPerHour: invoiceData.amountPerHour ?? "",
          gstRate: invoiceData.gstRate ?? 0,
          comment: invoiceData.comment || "",
        }],
      });
      setCreatedId(invoiceData._id);
      setAttachments(invoiceData.attachments || []);
    }
    setErrors({});
  }, [isOpen, isNew, invoiceData, initial, prefilledPropertyId, prefilledBuildingName]);

  useEffect(() => {
    if (!attachments.length) {
      setSignedUrls({});
      return;
    }
    const missing = attachments.filter((k) => !signedUrls[k]);
    if (!missing.length) return;
    (async () => {
      const urls = await getInvoiceSignedUrls(missing);
      setSignedUrls((prev) => ({ ...prev, ...urls }));
    })();
  }, [attachments]); // eslint-disable-line

  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  // ── Property line items (each becomes its own invoice on create) ───────────
  const propertyOptions = useMemo(
    () => (properties || []).map((p) => ({ value: p._id, label: p.title || p.name || p._id })),
    [properties]
  );

  // Accepts both the `building: [{buildingName}]` shape (internal property list)
  // and the `buildings: ["A", "B"]` shape (vendor eligibility list).
  const buildingOptionsFor = (propertyId) => {
    const prop = (properties || []).find((p) => p._id === propertyId);
    if (!prop) return [];
    const raw = Array.isArray(prop.buildings) && prop.buildings.length
      ? prop.buildings.map((b) => (typeof b === "string" ? b : b?.buildingName))
      : (prop.building || []).map((b) => b?.buildingName);
    return raw.filter(Boolean).map((b) => ({ value: b, label: b }));
  };

  const updateLine = (idx, patch) =>
    setForm((p) => ({
      ...p,
      lines: p.lines.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));

  const addLine = () =>
    setForm((p) => ({ ...p, lines: [...p.lines, blankLine()] }));

  const removeLine = (idx) =>
    setForm((p) => ({
      ...p,
      lines: p.lines.length > 1 ? p.lines.filter((_, i) => i !== idx) : p.lines,
    }));

  // When a property is chosen and it has exactly one building, auto-select it.
  const onPickProperty = (idx, propertyId) => {
    const buildings = buildingOptionsFor(propertyId);
    updateLine(idx, {
      propertyId,
      buildingName: buildings.length === 1 ? buildings[0].value : "",
    });
  };

  // Vendor options (active vendors). When editing an invoice whose vendor is no
  // longer active, keep a synthetic option so the selection still shows.
  const vendorOpts = useMemo(() => {
    const list = (vendors || []).map((v) => ({
      value: v._id,
      label: v.name,
      categories:
        (v.categories && v.categories.length
          ? v.categories
          : v.category
            ? [v.category]
            : []) || [],
    }));
    if (!isNew && invoiceData?.vendorId && !list.some((o) => o.value === invoiceData.vendorId)) {
      list.unshift({
        value: invoiceData.vendorId,
        label: `${invoiceData.vendorName || "Vendor"} (inactive)`,
        categories: invoiceData.category ? [invoiceData.category] : [],
      });
    }
    return list;
  }, [vendors, isNew, invoiceData]);

  const selectedVendor = vendorOpts.find((o) => o.value === form.vendorId) || null;

  // Category choices: from the selected vendor (internal create), or the
  // eligibility-provided list (vendor self-submission).
  const categoryChoices = useMemo(() => {
    const fromVendor = vendorLocked ? vendorCategories : (selectedVendor?.categories || []);
    return (fromVendor || []).map((c) => ({ value: c._id, label: c.name }));
  }, [vendorLocked, vendorCategories, selectedVendor]);

  // Keep the chosen category consistent with the available choices: drop a stale
  // category when the vendor changes, and auto-select when only one is available.
  useEffect(() => {
    setForm((p) => {
      const valid = categoryChoices.some((o) => o.value === p.categoryId);
      let categoryId = valid ? p.categoryId : "";
      if (!categoryId && categoryChoices.length === 1) categoryId = categoryChoices[0].value;
      return categoryId === p.categoryId ? p : { ...p, categoryId };
    });
  }, [categoryChoices]); // eslint-disable-line

  // Live GST + total breakdown across every line.
  const lineMoney = (form.lines || []).map((r) =>
    computeLineMoney(computeLineTotal(r.hours, r.amountPerHour), r.gstRate)
  );
  const subtotalSum = lineMoney.reduce((a, m) => a + (m.subtotal || 0), 0);
  const gstSum = lineMoney.reduce((a, m) => a + (m.gstAmount || 0), 0);
  const totalSum = lineMoney.reduce((a, m) => a + (m.total || 0), 0);

  const validate = () => {
    const e = {};
    if (!form.title?.trim()) e.title = "Title is required";
    if (!vendorLocked && !form.vendorId) e.vendorId = "Vendor is required";
    if (!form.categoryId) e.categoryId = "Category is required";
    const rows = form.lines || [];
    if (!rows.length) {
      e.lines = "Add at least one property";
    } else {
      const seen = new Set();
      for (const r of rows) {
        if (!r.propertyId) { e.lines = "Select a property for every line"; break; }
        if (!r.buildingName?.trim()) { e.lines = "Choose a building for every line"; break; }
        if (r.hours === "" || !Number.isInteger(Number(r.hours)) || Number(r.hours) < 0) {
          e.lines = "Enter whole-number hours for every line"; break;
        }
        if (r.amountPerHour === "" || Number.isNaN(Number(r.amountPerHour)) || Number(r.amountPerHour) < 0) {
          e.lines = "Enter a valid amount per hour for every line"; break;
        }
        const rate = Number(r.gstRate);
        if (Number.isNaN(rate) || rate < 0 || rate > 100) {
          e.lines = "GST must be between 0 and 100%"; break;
        }
        const key = `${r.propertyId}::${r.buildingName.trim().toLowerCase()}`;
        if (seen.has(key)) { e.lines = "Remove duplicate property / building lines"; break; }
        seen.add(key);
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (submitLock.current) return;
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    if (pendingFiles.some((p) => p.uploading)) {
      toast.warning("Please wait for the files to finish uploading.");
      return;
    }
    submitLock.current = true;
    setSaving(true);
    try {
      const stagedKeys = pendingFiles.map((p) => p.key).filter(Boolean);
      if (isNew && !createdId) {
        const payload = {
          title: form.title.trim(),
          description: form.description.trim(),
          referenceNo: form.referenceNo.trim(),
          lines: form.lines.map((r) => ({
            propertyId: r.propertyId,
            buildingName: r.buildingName.trim(),
            unitNo: (r.unitNo || "").trim(),
            hours: Number(r.hours),
            amountPerHour: Number(r.amountPerHour),
            gstRate: Number(r.gstRate),
            comment: (r.comment || "").trim(),
          })),
          attachments: stagedKeys,
          categoryId: form.categoryId,
        };
        if (!vendorLocked) payload.vendorId = form.vendorId;
        const res = await createInvoice(payload);
        const created = Array.isArray(res?.data) ? res.data : (res?.data ? [res.data] : []);
        if (!created.length) {
          toast.error("Invoice was not created.");
          return;
        }
        setPendingFiles([]);
        toast.success(
          created.length > 1
            ? `Submitted ${created.length} invoices under Invoice #${res.invoiceNo || ""}.`
            : "Invoice submitted for approval."
        );
        onClose(true);
      } else {
        const row = form.lines[0];
        const payload = {
          title: form.title.trim(),
          description: form.description.trim(),
          referenceNo: form.referenceNo.trim(),
          hours: Number(row.hours),
          amountPerHour: Number(row.amountPerHour),
          gstRate: Number(row.gstRate),
          comment: (row.comment || "").trim(),
          unitNo: (row.unitNo || "").trim(),
          properties: [{ propertyId: row.propertyId, buildingName: row.buildingName.trim(), unitNo: (row.unitNo || "").trim() }],
          categoryId: form.categoryId,
        };
        if (!vendorLocked) payload.vendorId = form.vendorId;
        const targetId = invoiceData?._id || createdId;
        await updateInvoice(targetId, payload);
        toast.success("Invoice updated.");
        onClose(true);
      }
    } catch {
      // toast handled in api helper
    } finally {
      setSaving(false);
      submitLock.current = false;
    }
  };

  const invoiceId = createdId || invoiceData?._id;

  const handleFiles = async (files) => {
    if (!files?.length) return;

    if (!invoiceId) {
      // New invoice: upload each file to a temporary staging area right away so it
      // gets a signed URL and previews exactly like a saved attachment. The keys
      // are adopted by the invoice on create.
      const toStage = Array.from(files).map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        name: file.name,
        key: null,
        signedUrl: null,
        uploading: true,
        error: false,
      }));
      setPendingFiles((prev) => [...prev, ...toStage]);
      if (fileInputRef.current) fileInputRef.current.value = "";

      await Promise.all(
        toStage.map(async (pf) => {
          try {
            const key = await uploadStagingFile(pf.file);
            const urls = await getStagingSignedUrls([key]);
            setPendingFiles((prev) =>
              prev.map((p) =>
                p.id === pf.id ? { ...p, key, signedUrl: urls[key] || null, uploading: false } : p
              )
            );
          } catch (err) {
            console.error("staging upload failed", err);
            setPendingFiles((prev) =>
              prev.map((p) => (p.id === pf.id ? { ...p, uploading: false, error: true } : p))
            );
            toast.error(`Failed to upload ${pf.name}`);
          }
        })
      );
      return;
    }

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        try {
          const key = await uploadFileToInvoice(invoiceId, file);
          setAttachments((prev) => [...prev, key]);
        } catch (err) {
          console.error("upload failed", err);
        }
      }
      toast.success("Files uploaded");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemovePending = (id) => {
    setPendingFiles((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed?.key) removeStagingObject(removed.key).catch(() => {});
      return prev.filter((p) => p.id !== id);
    });
  };

  const openPendingPreview = (pf) => {
    if (pf?.signedUrl) setPreviewPendingId(pf.id);
  };

  const handleRemove = async (key) => {
    try {
      await removeInvoiceAttachment(invoiceId, key);
      setAttachments((prev) => prev.filter((k) => k !== key));
      toast.success("Attachment removed");
    } catch {
      // handled
    }
  };

  // Header context shows the first line's property (with a "+N more" hint when the
  // submission spans several properties); the full editor lives in the body.
  const linkedCount = (form.lines || []).filter((r) => r.propertyId).length;
  const firstRow = (form.lines || []).find((r) => r.propertyId) || null;
  const firstPropLabel = firstRow
    ? propertyOptions.find((o) => o.value === firstRow.propertyId)?.label || ""
    : "";
  const headerPropertyLabel = firstPropLabel
    ? `${firstPropLabel}${linkedCount > 1 ? ` +${linkedCount - 1} more` : ""}`
    : (isNew ? prefilledPropertyLabel || "" : "");
  const headerBuildingName = linkedCount > 1 ? "" : (firstRow?.buildingName || "");

  const handleClose = (refresh) => {
    if (isNew && !createdId) cleanupStaged(pendingFiles);
    setPendingFiles([]);
    onClose(refresh);
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={() => handleClose(false)} size="lg" centered backdrop="static">
        <ModalHeader toggle={() => handleClose(false)} style={{ borderBottom: "1px solid #EEF2F7" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FeatherIcon icon={isNew ? "plus-circle" : "edit-2"} size={19} color="#4F46E5" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.2 }}>
                {isNew ? "New Invoice" : `Edit Invoice ${invoiceData?.invoiceNo ? `#${invoiceData.invoiceNo}` : ""}`}
              </div>
              {(headerPropertyLabel || headerBuildingName) ? (
                <div style={{
                  display: "flex", alignItems: "center", gap: 6, marginTop: 4,
                  fontSize: "0.76rem", color: "#475569", fontWeight: 600,
                }}>
                  <FeatherIcon icon="map-pin" size={12} color="#4F46E5" style={{ flexShrink: 0 }} />
                  <span style={{ color: "#0F172A" }}>{headerPropertyLabel || "—"}</span>
                  {headerBuildingName && (
                    <>
                      <span style={{ color: "#CBD5E1" }}>·</span>
                      <FeatherIcon icon="home" size={12} color="#64748B" style={{ flexShrink: 0 }} />
                      <span>{headerBuildingName}</span>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: "0.75rem", color: "#64748B", fontWeight: 500, marginTop: 2 }}>
                  {isNew ? "Fill in details and attach files in one go" : "Update invoice details"}
                </div>
              )}
            </div>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: "20px 24px" }}>
          <div className="row g-3">
            <div className="col-md-8">
              <Label style={LABEL_STYLE}>Invoice Title <span style={{ color: "#DC2626" }}>*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => set("title")(e.target.value)}
                placeholder="e.g. February cleaning service"
                style={INPUT_STYLE}
              />
              {errors.title && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.title}</div>}
            </div>
            <div className="col-md-4">
              <Label style={LABEL_STYLE}>Invoice No.</Label>
              <Input
                value={form.referenceNo}
                onChange={(e) => set("referenceNo")(e.target.value)}
                placeholder="Vendor invoice #"
                style={INPUT_STYLE}
              />
            </div>

            <div className="col-md-6">
              <Label style={LABEL_STYLE}>Vendor <span style={{ color: "#DC2626" }}>*</span></Label>
              {vendorLocked ? (
                <div style={{
                  ...INPUT_STYLE, display: "flex", alignItems: "center", gap: 8,
                  padding: "0 12px", background: "#F8FAFC", color: "#0F172A", fontWeight: 600,
                }}>
                  <FeatherIcon icon="briefcase" size={14} color="#4F46E5" />
                  {vendorName || "Your vendor account"}
                </div>
              ) : (
                <Select
                  options={vendorOpts}
                  value={selectedVendor}
                  onChange={(opt) => set("vendorId")(opt?.value || "")}
                  styles={selectStyles}
                  placeholder={vendorOpts.length ? "Choose vendor" : "No vendors yet — add one first"}
                  isDisabled={vendorOpts.length === 0}
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              )}
              {errors.vendorId && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.vendorId}</div>}
            </div>
            <div className="col-md-6">
              <Label style={LABEL_STYLE}>Category <span style={{ color: "#DC2626" }}>*</span></Label>
              <Select
                options={categoryChoices}
                value={categoryChoices.find((o) => o.value === form.categoryId) || null}
                onChange={(opt) => set("categoryId")(opt?.value || "")}
                styles={selectStyles}
                placeholder={
                  categoryChoices.length
                    ? "Choose category"
                    : (form.vendorId || vendorLocked ? "Vendor has no categories" : "Pick a vendor first")
                }
                isDisabled={categoryChoices.length === 0}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.categoryId && <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.categoryId}</div>}
            </div>

            {/* Property line items — each becomes its own invoice sharing one
                Invoice No. Per-property amount + editable GST. */}
            <div className="col-12">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <Label style={{ ...LABEL_STYLE, marginBottom: 0 }}>
                  Properties &amp; Costing <span style={{ color: "#DC2626" }}>*</span>
                </Label>
              </div>

              {isNew && (form.lines || []).length > 1 && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
                  background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 8,
                  padding: "7px 12px", fontSize: "0.74rem", color: "#3730A3",
                }}>
                  <FeatherIcon icon="info" size={14} style={{ flexShrink: 0 }} />
                  <span>Each property becomes a separate invoice — they share one Invoice No. and are approved independently.</span>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(form.lines || []).map((row, idx) => {
                  const buildingOpts = buildingOptionsFor(row.propertyId);
                  const subtotal = computeLineTotal(row.hours, row.amountPerHour);
                  const m = computeLineMoney(subtotal, row.gstRate);
                  return (
                    <div key={idx} style={{
                      display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap",
                      background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 10,
                    }}>
                      <div style={{ flex: "1 1 160px", minWidth: 150 }}>
                        <div style={{ fontSize: "0.66rem", color: "#64748B", fontWeight: 700, marginBottom: 3 }}>PROPERTY</div>
                        <Select
                          options={propertyOptions}
                          value={propertyOptions.find((o) => o.value === row.propertyId) || null}
                          onChange={(opt) => onPickProperty(idx, opt?.value || "")}
                          styles={selectStyles}
                          placeholder="Select property"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                      <div style={{ flex: "1 1 130px", minWidth: 120 }}>
                        <div style={{ fontSize: "0.66rem", color: "#64748B", fontWeight: 700, marginBottom: 3 }}>BUILDING</div>
                        <Select
                          options={buildingOpts}
                          value={buildingOpts.find((o) => o.value === row.buildingName) || null}
                          onChange={(opt) => updateLine(idx, { buildingName: opt?.value || "" })}
                          styles={selectStyles}
                          placeholder={row.propertyId ? "Select building" : "Pick property first"}
                          isDisabled={!row.propertyId}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                      <div style={{ flex: "0 0 90px" }}>
                        <div style={{ fontSize: "0.66rem", color: "#64748B", fontWeight: 700, marginBottom: 3 }}>UNIT NO</div>
                        <Input
                          value={row.unitNo}
                          onChange={(e) => updateLine(idx, { unitNo: e.target.value })}
                          placeholder="e.g. 101"
                          style={INPUT_STYLE}
                        />
                      </div>
                      <div style={{ flex: "0 0 80px" }}>
                        <div style={{ fontSize: "0.66rem", color: "#64748B", fontWeight: 700, marginBottom: 3 }}>HOURS</div>
                        <Input
                          type="number" step="1" min="0"
                          value={row.hours}
                          onChange={(e) => updateLine(idx, { hours: e.target.value.replace(/[^0-9]/g, "") })}
                          placeholder="0"
                          style={INPUT_STYLE}
                        />
                      </div>
                      <div style={{ flex: "0 0 100px" }}>
                        <div style={{ fontSize: "0.66rem", color: "#64748B", fontWeight: 700, marginBottom: 3 }}>AMT / HR</div>
                        <Input
                          type="number" step="0.01" min="0"
                          value={row.amountPerHour}
                          onChange={(e) => updateLine(idx, { amountPerHour: e.target.value })}
                          placeholder="0.00"
                          style={INPUT_STYLE}
                        />
                      </div>
                      <div style={{ flex: "0 0 70px" }}>
                        <div style={{ fontSize: "0.66rem", color: "#64748B", fontWeight: 700, marginBottom: 3 }}>GST %</div>
                        <Input
                          type="number" step="0.1" min="0" max="100"
                          value={row.gstRate}
                          onChange={(e) => updateLine(idx, { gstRate: e.target.value })}
                          placeholder="0"
                          style={INPUT_STYLE}
                        />
                      </div>
                      <div style={{ flex: "0 0 110px" }}>
                        <div style={{ fontSize: "0.66rem", color: "#64748B", fontWeight: 700, marginBottom: 3 }}>TOTAL</div>
                        <div style={{
                          height: 38, display: "flex", alignItems: "center", justifyContent: "flex-end",
                          fontWeight: 700, color: "#0F172A", fontSize: "0.84rem",
                        }}>
                          {formatCurrency(m.total)}
                        </div>
                      </div>
                      <div style={{ flex: "1 1 100%", marginTop: 2 }}>
                        <div style={{ fontSize: "0.66rem", color: "#64748B", fontWeight: 700, marginBottom: 3 }}>COMMENTS</div>
                        <Input
                          value={row.comment}
                          onChange={(e) => updateLine(idx, { comment: e.target.value })}
                          placeholder="Optional note for this line"
                          style={INPUT_STYLE}
                        />
                      </div>
                      {isNew && (
                        <button
                          type="button"
                          onClick={() => removeLine(idx)}
                          disabled={(form.lines || []).length <= 1}
                          title="Remove"
                          style={{
                            flexShrink: 0, width: 34, height: 34, borderRadius: 8, marginTop: 4,
                            border: "1px solid #FECDD3", background: "#FEF2F2",
                            color: "#DC2626", display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: (form.lines || []).length <= 1 ? "not-allowed" : "pointer",
                            opacity: (form.lines || []).length <= 1 ? 0.5 : 1,
                          }}
                        >
                          <FeatherIcon icon="trash-2" size={14} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {isNew && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={addLine}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      background: "#EEF2FF", border: "1px solid #C7D2FE", color: "#4F46E5",
                      borderRadius: 7, padding: "6px 12px", fontSize: "0.74rem", fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    <FeatherIcon icon="plus" size={12} /> Add property
                  </button>
                </div>
              )}
              {errors.lines && (
                <div style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 4 }}>{errors.lines}</div>
              )}

              {/* Totals summary */}
              <div style={{
                display: "flex", justifyContent: "flex-end", gap: 24, flexWrap: "wrap",
                marginTop: 10, padding: "10px 14px", background: "#0F172A", borderRadius: 10,
              }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "0.64rem", color: "#94A3B8", fontWeight: 700, textTransform: "uppercase" }}>Subtotal</div>
                  <div style={{ fontSize: "0.9rem", color: "#E2E8F0", fontWeight: 600 }}>{formatCurrency(subtotalSum)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "0.64rem", color: "#94A3B8", fontWeight: 700, textTransform: "uppercase" }}>GST</div>
                  <div style={{ fontSize: "0.9rem", color: "#E2E8F0", fontWeight: 600 }}>{formatCurrency(gstSum)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "0.64rem", color: "#A5B4FC", fontWeight: 700, textTransform: "uppercase" }}>Grand Total</div>
                  <div style={{ fontSize: "1.05rem", color: "#fff", fontWeight: 800 }}>{formatCurrency(totalSum)}</div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <Label style={LABEL_STYLE}>Additional Notes</Label>
              <Input
                type="textarea"
                rows={5}
                value={form.description}
                onChange={(e) => set("description")(e.target.value)}
                placeholder="Add any extra context, scope, or instructions for this invoice…"
                style={{ ...INPUT_STYLE, height: "auto", minHeight: 110, padding: "10px 12px" }}
              />
            </div>

            {/* Attachments — staged before create, uploaded after. All document types allowed. */}
            <div className="col-12">
              <div style={{
                background: "#F8FAFC", border: "1px dashed #CBD5E1", borderRadius: 10,
                padding: 16, marginTop: 6,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.85rem" }}>
                      <FeatherIcon icon="paperclip" size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
                      Invoice Files & Attachments
                    </div>
                    <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 2 }}>
                      Upload the original invoice PDF, photos, receipts, or supporting documents.
                    </div>
                  </div>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: "none" }}
                      onChange={(e) => handleFiles(e.target.files)}
                    />
                    <button
                      type="button"
                      className="iv-btn iv-btn--primary"
                      disabled={uploading || saving}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? <Spinner size="sm" /> : <FeatherIcon icon={invoiceId ? "upload" : "plus"} size={14} />}
                      {invoiceId ? "Upload" : "Add Files"}
                    </button>
                  </div>
                </div>
                {attachments.length === 0 && pendingFiles.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "16px 0", color: "#94A3B8", fontSize: "0.8rem" }}>
                    No attachments yet
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                    {/* Pending (uploaded to staging, not yet on the invoice) */}
                    {pendingFiles.map((pf) => {
                      const filename = pf.name;
                      const t = getFileTypeFromKey(filename);
                      const canPreview = !!pf.signedUrl && !pf.uploading;
                      return (
                        <div key={pf.id} className="iv-attach-card">
                          <div
                            className="iv-attach-card__preview"
                            onClick={() => canPreview && openPendingPreview(pf)}
                            style={{ cursor: canPreview ? "zoom-in" : "default" }}
                          >
                            {pf.uploading
                              ? <Spinner size="sm" style={{ color: "#4F46E5" }} />
                              : t === "image" && pf.signedUrl
                                ? <img src={pf.signedUrl} alt={filename} />
                                : t === "pdf"
                                  ? <FeatherIcon icon="file-text" size={32} color="#DC2626" />
                                  : t === "office"
                                    ? <FeatherIcon icon="file" size={32} color="#2563EB" />
                                    : <FeatherIcon icon="file" size={32} color="#64748B" />}
                            {pf.uploading && <span className="iv-attach-card__badge">UPLOADING</span>}
                          </div>
                          <div className="iv-attach-card__name" title={filename}>{filename}</div>
                          <div className="iv-attach-card__actions">
                            {canPreview && (
                              <button
                                type="button"
                                onClick={() => openPendingPreview(pf)}
                                title="Preview"
                              >
                                <FeatherIcon icon="eye" size={12} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemovePending(pf.id)}
                              title="Remove"
                              style={{ color: "#DC2626" }}
                              disabled={saving || pf.uploading}
                            >
                              <FeatherIcon icon="trash-2" size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* Server-confirmed attachments */}
                    {attachments.map((key) => {
                      const filename = key.split("/").pop();
                      const url = signedUrls[key];
                      const t = getFileTypeFromKey(key);
                      return (
                        <div key={key} className="iv-attach-card">
                          <div className="iv-attach-card__preview" onClick={() => url && setPreviewKey(key)}>
                            {t === "image" && url
                              ? <img src={url} alt={filename} />
                              : t === "pdf"
                                ? <FeatherIcon icon="file-text" size={32} color="#DC2626" />
                                : t === "office"
                                  ? <FeatherIcon icon="file" size={32} color="#2563EB" />
                                  : <FeatherIcon icon="file" size={32} color="#64748B" />}
                          </div>
                          <div className="iv-attach-card__name" title={filename}>{filename}</div>
                          <div className="iv-attach-card__actions">
                            <button
                              type="button"
                              onClick={() => url && setPreviewKey(key)}
                              title="Preview"
                            >
                              <FeatherIcon icon="eye" size={12} />
                            </button>
                            {url && (
                              <a href={url} target="_blank" rel="noopener noreferrer" title="Download">
                                <FeatherIcon icon="download" size={12} />
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemove(key)}
                              title="Remove"
                              style={{ color: "#DC2626" }}
                            >
                              <FeatherIcon icon="trash-2" size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter style={{ borderTop: "1px solid #EEF2F7" }}>
          <Button
            color="secondary"
            outline
            onClick={() => handleClose(false)}
            disabled={saving || uploading}
          >
            Cancel
          </Button>
          <button
            type="button"
            className="iv-btn iv-btn--primary"
            onClick={handleSave}
            disabled={saving || uploading || pendingFiles.some((p) => p.uploading)}
          >
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={14} />}
            {isNew ? "Create Invoice" : "Save"}
          </button>
        </ModalFooter>
      </Modal>

      <FilePreviewModal
        isOpen={!!previewKey}
        toggle={() => setPreviewKey(null)}
        signedUrl={previewKey ? signedUrls[previewKey] : null}
        fileKey={previewKey}
      />

      <FilePreviewModal
        isOpen={!!previewPendingId}
        toggle={() => setPreviewPendingId(null)}
        signedUrl={previewPendingId
          ? pendingFiles.find((p) => p.id === previewPendingId)?.signedUrl || null
          : null}
        fileKey={previewPendingId
          ? pendingFiles.find((p) => p.id === previewPendingId)?.name || null
          : null}
      />
    </>
  );
};

export { InvoiceFormModal };
